import { useMemo, useRef, useState } from 'react';
import { ChevronDown, Search } from 'lucide-react';
import { DOC_ICONS, docIcon } from '../lib/doc-icons';
import { R, shadow } from '../lib/ui';
import { AnchoredPopover } from './AnchoredPopover';

/** Кнопка с текущей иконкой и выпадающая сетка со всеми 103 вариантами. */
export function IconPicker({
  value, onChange, size = 40,
}: {
  value: string | null;
  onChange: (key: string) => void;
  size?: number;
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const ref = useRef<HTMLButtonElement>(null);

  const Current = docIcon(value);

  const shown = useMemo(() => {
    const term = query.trim().toLowerCase();
    return term ? DOC_ICONS.filter((i) => i.label.toLowerCase().includes(term)) : DOC_ICONS;
  }, [query]);

  return (
    <div style={{ position: 'relative', flex: `0 0 ${size}px` }}>
      <button
        ref={ref}
        type="button"
        onClick={() => setOpen((v) => !v)}
        title="Выбрать иконку"
        style={{
          width: size,
          height: size,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: R.bgInput,
          border: `1px solid ${open ? R.accentBorder : R.border}`,
          color: R.accent,
          position: 'relative',
        }}
      >
        <Current size={Math.round(size * 0.5)} />
        <ChevronDown
          size={10}
          style={{ position: 'absolute', right: 3, bottom: 3, color: R.textMuted }}
        />
      </button>

      <AnchoredPopover anchorRef={ref} open={open} onClose={() => setOpen(false)}>
        <div
          style={{
            width: 320,
            background: R.bgPanel,
            border: `2px solid ${R.border}`,
            boxShadow: shadow.dropdown,
            padding: 8,
          }}
        >
          <div style={{ position: 'relative', marginBottom: 8 }}>
            <Search
              size={13}
              color={R.textMuted}
              style={{ position: 'absolute', left: 8, top: '50%', transform: 'translateY(-50%)' }}
            />
            <input
              autoFocus
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Поиск…"
              style={{
                width: '100%',
                padding: '7px 8px 7px 26px',
                fontSize: 13,
                border: `1px solid ${R.border}`,
                background: R.bgInput,
                color: R.text,
                boxSizing: 'border-box',
              }}
            />
          </div>

          <div
            className="rt-scroll"
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(8, 1fr)',
              gap: 2,
              maxHeight: 260,
              overflowY: 'auto',
            }}
          >
            {shown.map(({ key, label, Component }) => {
              const active = key === (value ?? 'file');
              return (
                <button
                  key={key}
                  type="button"
                  title={label}
                  onClick={() => {
                    onChange(key);
                    setOpen(false);
                  }}
                  style={{
                    display: 'grid',
                    placeItems: 'center',
                    height: 34,
                    color: active ? R.accent : R.textSecondary,
                    background: active ? R.accentSubtle : 'transparent',
                  }}
                  onMouseEnter={(e) => {
                    if (!active) e.currentTarget.style.background = R.bgElevated;
                  }}
                  onMouseLeave={(e) => {
                    if (!active) e.currentTarget.style.background = 'transparent';
                  }}
                >
                  <Component size={16} />
                </button>
              );
            })}
          </div>

          {shown.length === 0 && (
            <div style={{ padding: 12, fontSize: 12, color: R.textMuted, textAlign: 'center' }}>
              Ничего не найдено
            </div>
          )}
        </div>
      </AnchoredPopover>
    </div>
  );
}
